// BookingDetailPage — read-only view of a booking and its multi-vehicle
// siblings, plus the audit log (booking_logs) and a receipt link if the
// booking has been depolluted.
//
// Header actions:
//   - Edit  → /bookings/:enc_id/edit
//   - Print → window.print() via <PrintButton/> (no PDF endpoint yet —
//             tracked as gap §9.12 family for bookings)
//   - Cancel booking → opens CancelBookingModal

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Link2, Pencil, RotateCcw } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { PrintButton } from '../../components/ui/PrintButton/PrintButton';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { CancelBookingModal } from './components/CancelBookingModal';
import { CancellationLinkModal } from './components/CancellationLinkModal';
import { ReinstateBookingModal } from '../cancellations/components/ReinstateBookingModal';
import { useBookingDetail } from './hooks/useBookingDetail';
import { readCancellationLink } from '../../api/bookings';
import { queryKeys } from '../../api/queryKeys';
import type { BookingStatus, BookingSummary } from '../../types/api';
import styles from './BookingDetailPage.module.css';

export const BOOKING_STATUS_VARIANTS: Record<BookingStatus, BadgeVariant> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'danger',
  cancellation_requested: 'warning',
};

const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  cancellation_requested: 'Cancellation requested',
};

function formatPrice(p: number): string {
  return `£${p.toFixed(2)}`;
}

function formatDate(dateIso: string): string {
  if (!dateIso) return '—';
  const d = new Date(`${dateIso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateIso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function BookingDetailPage(): JSX.Element {
  const { enc_id: encId } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reinstateOpen, setReinstateOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkExists, setLinkExists] = useState(false);

  const detail = useBookingDetail(encId);

  // Probe whether a cancellation link is currently active so the action
  // button can flip its label between "Generate" and "View". This does NOT
  // return the raw token — the staff member must click to issue a fresh
  // one (server stores only the SHA-256 hash).
  const bookingStatus = detail.data?.booking.status ?? null;
  useEffect(() => {
    if (!encId) return;
    if (bookingStatus === null) return;
    if (
      bookingStatus === 'completed' ||
      bookingStatus === 'cancelled' ||
      bookingStatus === 'cancellation_requested'
    ) {
      setLinkExists(false);
      return;
    }
    let cancelled = false;
    void readCancellationLink(encId)
      .then((res) => {
        if (cancelled) return;
        setLinkExists(res !== null && res.used_at === null);
      })
      .catch(() => {
        if (!cancelled) setLinkExists(false);
      });
    return () => {
      cancelled = true;
    };
  }, [encId, bookingStatus]);

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spinner size="md" />
          <span>Loading booking…</span>
        </div>
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Booking" subtitle="Could not load this booking." />
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    );
  }

  const { booking, related_vehicles: related, logs, receipt } = detail.data;
  const allVehicles: BookingSummary[] = [booking, ...related];
  const cancellable =
    booking.status === 'pending' ||
    booking.status === 'in_progress' ||
    booking.status === 'cancellation_requested';
  const reinstatable = booking.status === 'cancelled';
  const canGenerateCancellationLink =
    booking.status !== 'completed' &&
    booking.status !== 'cancelled' &&
    booking.status !== 'cancellation_requested';

  function handleCancelled() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(encId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
    setCancelOpen(false);
  }

  function handleReinstated() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(encId ?? '') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
    setReinstateOpen(false);
  }

  const statusLabel = BOOKING_STATUS_LABELS[booking.status];
  const statusVariant = BOOKING_STATUS_VARIANTS[booking.status];

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Booking ${booking.reference}`}
        subtitle={
          <span className={styles.subtitle}>
            <StatusBadge
              status={booking.status}
              statusMap={BOOKING_STATUS_VARIANTS}
              formatLabel={(s) => BOOKING_STATUS_LABELS[s as BookingStatus]}
            />
            <span data-testid="booking-created">
              Created {formatDateTime(booking.created_at)}
            </span>
          </span>
        }
        actions={
          <div className={styles.actions} data-print-hide>
            <Button
              variant="secondary"
              icon={Pencil}
              onClick={() => navigate(`/collections/bookings/${encodeURIComponent(booking.enc_id)}/edit`)}
            >
              Edit
            </Button>
            <PrintButton onPrint={() => window.print()} />
            {canGenerateCancellationLink ? (
              <Button
                variant="secondary"
                icon={Link2}
                onClick={() => setLinkModalOpen(true)}
              >
                {linkExists ? 'View cancellation link' : 'Generate cancellation link'}
              </Button>
            ) : null}
            {cancellable ? (
              <Button variant="danger" onClick={() => setCancelOpen(true)}>
                Cancel booking
              </Button>
            ) : null}
            {reinstatable ? (
              <Button
                variant="primary"
                icon={RotateCcw}
                onClick={() => setReinstateOpen(true)}
              >
                Reinstate booking
              </Button>
            ) : null}
          </div>
        }
      />

      <Card title="Collection details">
        <dl className={styles.dl}>
          <div>
            <dt>Date</dt>
            <dd>{formatDate(booking.date)} · {booking.timeframe}</dd>
          </div>
          <div>
            <dt>Driver</dt>
            <dd>{booking.driver_name ?? '—'}</dd>
          </div>
          <div>
            <dt>Address</dt>
            <dd>
              {booking.effective_address.address}
              <br />
              <span className={styles.postcode}>
                {booking.effective_address.postcode}
              </span>
              {booking.effective_address.is_collection ? (
                <Badge variant="info">Collection address</Badge>
              ) : null}
            </dd>
          </div>
          <div>
            <dt>Work source</dt>
            <dd>{booking.source_name ?? '—'}</dd>
          </div>
          <div>
            <dt>Payment mode</dt>
            <dd>{booking.payment_mode_name ?? '—'}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <Badge variant={statusVariant}>{statusLabel}</Badge>
            </dd>
          </div>
        </dl>
      </Card>

      <Card title={`Vehicle${allVehicles.length > 1 ? 's' : ''}`}>
        <div className={styles.vehicles}>
          {allVehicles.map((v, i) => (
            <article key={v.enc_id} className={styles.vehicleCard}>
              <header className={styles.vehicleHead}>
                <h3>
                  Vehicle {i + 1} of {allVehicles.length}
                </h3>
                <span className={styles.regChip}>{v.car_registration}</span>
              </header>
              <dl className={styles.dl}>
                <div>
                  <dt>Make and model</dt>
                  <dd>{v.car_make_model}</dd>
                </div>
                <div>
                  <dt>Scrap type</dt>
                  <dd>{v.product_name ?? '—'}</dd>
                </div>
                <div>
                  <dt>Price</dt>
                  <dd>{formatPrice(v.price)}</dd>
                </div>
                <div>
                  <dt>Paid / collected</dt>
                  <dd>
                    {v.paid ? <Badge variant="success">Paid</Badge> : <Badge variant="muted">Unpaid</Badge>}{' '}
                    {v.collected ? (
                      <Badge variant="success">Collected</Badge>
                    ) : (
                      <Badge variant="muted">Not collected</Badge>
                    )}
                  </dd>
                </div>
                {v.notes ? (
                  <div className={styles.fullWidth}>
                    <dt>Per-vehicle notes</dt>
                    <dd>{v.notes}</dd>
                  </div>
                ) : null}
              </dl>
            </article>
          ))}
        </div>
      </Card>

      <Card title="Customer">
        <dl className={styles.dl}>
          <div>
            <dt>Name</dt>
            <dd>
              {booking.customer_uuid ? (
                <Link to={`/customers/${encodeURIComponent(booking.customer_uuid)}`}>
                  {booking.name}
                </Link>
              ) : (
                booking.name
              )}
            </dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>
              <a href={`tel:${booking.phone}`}>{booking.phone}</a>
              {booking.phone2 ? <> · <a href={`tel:${booking.phone2}`}>{booking.phone2}</a></> : null}
            </dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>
              {booking.email ? <a href={`mailto:${booking.email}`}>{booking.email}</a> : '—'}
            </dd>
          </div>
          <div>
            <dt>Address</dt>
            <dd>
              {booking.address}
              <br />
              <span className={styles.postcode}>{booking.postcode}</span>
            </dd>
          </div>
        </dl>
      </Card>

      {(booking.notes || booking.driver_notes) ? (
        <Card title="Notes">
          {booking.notes ? (
            <div className={styles.notesBlock}>
              <h4>Booking notes</h4>
              <p>{booking.notes}</p>
            </div>
          ) : null}
          {booking.driver_notes ? (
            <div className={styles.notesBlock}>
              <h4>Driver notes</h4>
              <p>{booking.driver_notes}</p>
            </div>
          ) : null}
        </Card>
      ) : null}

      {receipt ? (
        <Card title="Linked receipt">
          <p>
            This booking has been depolluted. Receipt:{' '}
            <Link to={`/collections/receipts/${encodeURIComponent(receipt.enc_id)}`}>
              {receipt.unique_id}
            </Link>
          </p>
        </Card>
      ) : null}

      <Card title="Activity log">
        {logs.length === 0 ? (
          <p className={styles.empty}>No activity recorded for this booking.</p>
        ) : (
          <ol className={styles.timeline}>
            {logs.map((entry) => (
              <li key={entry.uuid}>
                <span className={styles.timelineDate}>
                  {formatDateTime(entry.created_at)}
                </span>
                <span className={styles.timelineAction}>{entry.action}</span>
                {entry.performed_by_name ? (
                  <span className={styles.timelineActor}> · {entry.performed_by_name}</span>
                ) : null}
                {entry.details ? (
                  <p className={styles.timelineDetails}>{entry.details}</p>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Card>

      <CancelBookingModal
        open={cancelOpen}
        encId={booking.enc_id}
        reference={booking.reference}
        initialReason={booking.cancellation_reason ?? undefined}
        onClose={() => setCancelOpen(false)}
        onCancelled={handleCancelled}
      />

      <CancellationLinkModal
        open={linkModalOpen}
        encId={booking.enc_id}
        reference={booking.reference}
        onClose={() => {
          setLinkModalOpen(false);
          setLinkExists(true);
        }}
      />

      <ReinstateBookingModal
        open={reinstateOpen}
        booking={booking}
        onClose={() => setReinstateOpen(false)}
        onSuccess={handleReinstated}
      />
    </div>
  );
}
